/**
 * Where the account stands, in one card at the top of every tab (Customer 360, 02 §5.1; 01 §4.1; the owner's
 * polish of 24 Oct 2026). Each answer is said once, here, and nowhere else on the page:
 *
 *   where the figures come from   one quiet line: the source, read-only, when it was updated
 *   how much is owed              the balance, the largest figure on the page
 *   how much is actually overdue  the due-status bar and its rows: what, how much, how late
 *   what needs attention          one line, its reasons behind "Why this account"
 *   the work                      last contact · promise · next follow-up and who owns it · who to reach
 *
 * Overdue is money past an actual due date (`semantics.ts`). A sheet says it cannot know, an account with no bills
 * says so too, and an age is never offered in its place.
 *
 * Where there is no rail (below 1280px) the card is Overview's alone: Bills, Contacts and the full activity open
 * with `MoneyStrip`, the card in short, and then the tab itself (owner, 24 Oct 2026).
 */
import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import {
    ACTIVITY_LABELS, chequeState, describeAge, firstDialable, followUpWords, holds, isChequeActive, moneyFacts, overdueLines,
    type ActivityEntry, type Cheque, type MoneyFact,
} from '@timely/domain';
import { DrCr, Freshness, SegmentBar, cx, type FreshnessState } from '@timely/ui';
import { useFormatters } from '../../book/useFormatters';
import { useActiveWorkspace } from '../../workspace/WorkspaceProvider';
import { paths } from '../../workspace/navigation';
import { freshnessMessage, type FreshnessAudience } from '../../receivables/freshnessCopy';
import { useAccount } from './accountPage';
import { AttentionBrief, AttentionLine } from './Attention';
import { useTabTo } from './AccountTabs';

/** The square beside a row: the due-status colours the bar draws, navy for an age, none for an absence. */
const ROW_MARK: Partial<Record<MoneyFact['key'], string>> = {
    overdue: 'bg-due-past', grace: 'bg-due-past', not_yet_due: 'bg-due-later', no_due_date: 'bg-separator-strong',
    unbilled: 'bg-separator-strong', critical_age: 'bg-ageing-3',
};

/** The balance as the account's figure: how much, which side, and in a word what that means. */
function useBalance() {
    const { customer } = useAccount();
    const total = Math.abs(Number(customer.total) || 0);
    const inCredit = customer.totalType === 'Cr' && total > 0;
    return { total, inCredit, side: inCredit ? 'Cr' as const : 'Dr' as const, word: inCredit ? 'in credit' : total > 0 ? 'owed' : 'nothing owed' };
}

export function MoneyBand({ className }: { className?: string }) {
    const page = useAccount();
    const { money, date, count } = useFormatters();
    const { overdue } = page;
    const day = (d: string) => date.weekday(d);
    const facts = moneyFacts(overdue, { money: money.exact, day });
    const { total, inCredit, side, word } = useBalance();
    const bills = page.source === 'tally' && page.statement.statement?.billWise ? page.statement.statement.bills.length : null;

    const bar = overdue.kind === 'bills' && total > 0 ? [
        { key: 'overdue', amount: overdue.overdue.amount, fill: 'bg-due-past' },
        { key: 'not_yet_due', amount: overdue.notYetDue.amount, fill: 'bg-due-later' },
        { key: 'no_due_date', amount: overdue.noDueDate.amount + (overdue.unbilled?.amount ?? 0), fill: 'bg-separator-strong' },
    ] : null;
    const said = overdueLines(overdue, { money: money.exact, day });

    return (
        <section aria-label="Where this account stands" className={cx('rounded-card border border-separator bg-card shadow-e0 overflow-hidden', className)}>
            <div className="px-5 pt-4 pb-5" role="group" aria-label={page.source === 'tally' ? 'From TallyPrime' : 'From your receivables sheet'}>
                <Provenance />
                <p className="mt-2.5 flex items-baseline gap-x-2 gap-y-1 flex-wrap">
                    {/* Proportional figures on a standalone amount; the side beside it, exact, never a compact "Cr" (02 §11). */}
                    <span className="font-display stretch-title text-figure font-semibold text-label leading-none tracking-title">{money.exact(total)}</span>
                    {total > 0 && <DrCr side={side} />}
                    <span className="text-secondary text-label-2">
                        {word}
                        {bills !== null && total > 0 && !inCredit && <span className="text-label-3"> · {count(bills)} open {bills === 1 ? 'bill' : 'bills'}</span>}
                    </span>
                </p>
                {page.statementLoading ? (
                    <div aria-busy="true" className="mt-4 space-y-2.5">
                        <span className="sr-only">Reading the bills from TallyPrime</span>
                        <span aria-hidden="true" className="block h-2 max-w-xl rounded-full bg-card-3 animate-pulse" />
                        <span aria-hidden="true" className="block h-4 w-72 max-w-full rounded-sm bg-card-3 animate-pulse" />
                    </div>
                ) : <>
                    {bar && bar.some(p => p.amount > 0) && (
                        <SegmentBar className="mt-4 max-w-xl" parts={bar}
                            label={`${said.headline.text}. ${said.lines.map(l => l.text).join('. ')}`} />
                    )}
                    {facts.rows.length > 0 && (
                        <dl aria-label="The money" className={cx('grid grid-cols-[auto_auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 items-baseline max-w-2xl', bar ? 'mt-3' : 'mt-3.5')}>
                            {facts.rows.map(row => <Row key={`${row.key}:${row.label}`} row={row} />)}
                        </dl>
                    )}
                    {facts.notes.map(note => <p key={note} className="mt-2 text-caption text-label-3 max-w-2xl">{note}</p>)}
                </>}
            </div>
            <AttentionLine />
            <Work />
        </section>
    );
}

/**
 * The card in short, above Bills, Contacts and the full activity where there is no rail (owner, 24 Oct 2026): a tab
 * chosen on a phone, a tablet or a 1024px window opens at the tab, not at the top of Overview again. It keeps only what
 * holds the place: the balance, the overdue as the card says it, how current the figures are, and the attention line's
 * fact when it is an exception. The account's name is the page's heading, just above; the rest is Overview's.
 */
export function MoneyStrip({ className }: { className?: string }) {
    const page = useAccount();
    const { money, date } = useFormatters();
    const { total, side, word } = useBalance();
    const facts = moneyFacts(page.overdue, { money: money.exact, day: d => date.weekday(d) });
    // The overdue answer, in the card's own words; a balance in credit or at nil has none, as nothing is owed.
    const due = facts.rows.find(r => r.label === 'Overdue') ?? null;
    return (
        <section aria-label="This account, in short" className={cx('rounded-card border border-separator bg-card shadow-e0 overflow-hidden', className)}>
            <div className="@container px-4 py-3">
                <div className="flex items-baseline gap-x-5 gap-y-1.5 flex-wrap">
                    <p className="flex items-baseline gap-x-1.5 flex-wrap">
                        <span className="font-display stretch-title text-figure-sm font-semibold text-label leading-none tracking-title">{money.exact(total)}</span>
                        {total > 0 && <DrCr side={side} />}
                        <span className="text-secondary text-label-2">{word}</span>
                    </p>
                    {page.statementLoading ? (
                        <span aria-busy="true" className="inline-flex items-center">
                            <span className="sr-only">Reading the bills from TallyPrime</span>
                            <span aria-hidden="true" className="block h-4 w-36 rounded-sm bg-card-3 animate-pulse" />
                        </span>
                    ) : due && (
                        <p className="flex items-baseline gap-2 text-secondary min-w-0">
                            <span aria-hidden="true" className={cx('size-2 flex-none', due.amount ? ROW_MARK.overdue : 'bg-separator-strong')} />
                            {/* "Nothing past its due date" says it whole; the others are the word and its answer. */}
                            {due.key !== 'nothing_past_due' && <span className="text-label-2">Overdue</span>}
                            {due.amount ? <span className="font-semibold num text-dang">{due.amount}</span>
                                : due.key === 'nothing_past_due' ? <span className="text-label">{due.text}</span>
                                    : <span className={due.tone === 'muted' ? 'text-label-3' : 'text-label'}>{due.text.charAt(0).toLowerCase() + due.text.slice(1)}</span>}
                        </p>
                    )}
                    <div className="@min-[36rem]:ml-auto"><Provenance compact /></div>
                </div>
            </div>
            <AttentionBrief />
        </section>
    );
}

/**
 * A row of the money: what it is, the amount in figures (tabular, so a column of them lines up), and a few words
 * after it. A row with no amount states its fact across both.
 */
function Row({ row }: { row: MoneyFact }) {
    return (
        <Fragment>
            <dt className="flex items-center gap-2 text-secondary text-label-2 whitespace-nowrap">
                <span aria-hidden="true" className={cx('size-2 flex-none', ROW_MARK[row.key] ?? 'bg-transparent')} />
                {row.label}
            </dt>
            {row.amount ? (
                <>
                    <dd className={cx('text-secondary font-semibold num text-right whitespace-nowrap', row.tone === 'dang' ? 'text-dang' : 'text-label')}>{row.amount}</dd>
                    <dd className="text-secondary text-label-3 min-w-0">{row.text}</dd>
                </>
            ) : (
                <dd className={cx('col-span-2 text-secondary', row.tone === 'muted' ? 'text-label-3' : 'text-label')}>{row.text}</dd>
            )}
        </Fragment>
    );
}

/**
 * Where the figures come from, that Timely only reads them, and when they were last updated: one quiet line. It is
 * the account's freshness line (Phase 0): marked as one, and a change of state is said to a screen reader once, from a
 * live region that is there before anything changes — in the same words every other receivables screen uses. A stale
 * source colours the square; the attention line says what that means, so the words are said once. `compact` is the
 * strip's: when, and the square, alone.
 */
function Provenance({ compact = false }: { compact?: boolean }) {
    const page = useAccount();
    const context = useActiveWorkspace();
    const { date } = useFormatters();
    const f = page.freshness.freshness;
    const fmt = { day: date.long, time: date.time, dayKey: date.inputValue };
    const state: FreshnessState = page.freshness.unavailable ? 'none'
        : !f ? 'none' : f.problem ? 'stopped' : f.syncing ? 'syncing' : f.level === 'stale' || f.level === 'delayed' ? 'stale' : f.level === 'current' ? 'current' : 'none';
    const audience: FreshnessAudience = holds(context, 'datasource.manage') ? 'manage' : holds(context, 'datasource.view') ? 'view' : 'member';
    const message = freshnessMessage(page.freshness, audience, fmt);
    // "Updated 8 minutes ago", "Updated today at 12:52 pm", "Updated 22 Oct, 7:45 pm". Only what is true: nothing
    // while the answer is on its way, and nothing where no source is connected — never "not read yet" for a moment.
    const age = f?.updatedAt ? describeAge(f.updatedAt, page.freshness.now, fmt) : null;
    const when = age && f?.updatedAt && age.startsWith('on ') ? `${date.short(f.updatedAt)}, ${date.time(f.updatedAt)}` : age;
    const words = page.freshness.unavailable ? 'Could not check how current'
        : !f ? null : f.syncing ? 'Updating now' : when ? `Updated ${when}` : null;
    return (
        <div data-freshness={message?.tone ?? 'none'}>
            <div role="status" className="sr-only">{message?.announce ?? ''}</div>
            {compact ? words && <Freshness state={state} className="!text-caption !text-label-3 whitespace-nowrap">{words}</Freshness> : (
                /* The state's square sets "Updated …" apart, so no separator is left alone at the end or the start of a
                    line when a narrow screen wraps it. */
                <p className="flex items-center gap-x-3 gap-y-1 flex-wrap text-caption text-label-3 min-h-5">
                    <span>
                        <span className="font-semibold text-label-2">{page.source === 'tally' ? 'TallyPrime' : 'Receivables sheet'}</span>
                        <span aria-hidden="true"> · </span>
                        <span>Read-only</span>
                    </span>
                    {words && <Freshness state={state} className="!text-caption !text-label-3">{words}</Freshness>}
                </p>
            )}
        </div>
    );
}

/** The latest entry that is not a change note. */
function lastWorkEntry(entries: readonly ActivityEntry[]): ActivityEntry | null {
    return entries.find(e => e.kind !== 'system') ?? null;
}

/**
 * The work, on the tint: what was said last, what is promised, when the next follow-up is and who owns it, and whom
 * to reach. One grid, laid out by the room it has: four across on a wide workspace, two by two beside the rail, one
 * a row on a phone.
 */
function Work() {
    const page = useAccount();
    const { workspace } = useActiveWorkspace();
    const tabTo = useTabTo();
    const { money, date, today } = useFormatters();
    const { customer, row, entries, names, rights } = page;
    const last = lastWorkEntry(entries);
    const resolved = last?.resolvesId ? entries.find(e => e.id === last.resolvesId) : undefined;
    const lastLabel = last ? (resolved?.kind === 'promise' ? (last.kind === 'payment' ? 'Promise kept' : 'Promise not kept') : ACTIVITY_LABELS[last.kind]) : null;
    const firstName = (name: string) => name.split(/\s+/)[0] || name;

    // The open promise, or how the last one ended.
    const answered = customer.promiseOutcome ? latestAnsweredPromise(entries) : null;
    const promise = row.promise
        ? {
            word: row.promise.window === 'passed' ? 'Date passed, unanswered' : row.promise.window === 'today' ? 'Due today' : 'Open',
            tone: row.promise.window === 'passed' || row.promise.window === 'today' ? 'text-warn' : 'text-label',
            detail: `${money.exact(row.promise.amount)}${customer.forecastDate ? ` by ${date.weekday(customer.forecastDate)}` : ''}`,
        }
        : customer.promiseOutcome === 'not_kept'
            ? { word: 'Not kept', tone: 'text-dang', detail: answered ? `${money.exact(answered.amount)}${answered.on ? ` was due ${date.weekday(answered.on)}` : ''}` : 'The last promise' }
            : customer.promiseOutcome === 'kept'
                ? { word: 'Kept', tone: 'text-pos', detail: answered ? `${money.exact(answered.amount)}${answered.on ? ` promised for ${date.weekday(answered.on)}` : ''}` : 'The last promise' }
                : null;

    // The next follow-up, and who owns it.
    const next = followUpWords(row.followUp, row.daysToFollowUp, customer.followUpDate ? date.weekday(customer.followUpDate) : '');
    const collector = customer.collectorMemberId ? names.get(customer.collectorMemberId as string) : undefined;
    const owner = customer.ownerMemberId ? names.get(customer.ownerMemberId as string) : undefined;
    const who = collector
        ? `${collector}, collector${owner && owner !== collector ? ` · owner ${owner}` : ''}`
        : owner ? `${owner}, owner` : 'Nobody is assigned';

    // Whom Call rings: the first dialable number in the account's own order. Call itself is in the toolbar.
    const contact = firstDialable(customer);
    const inHand = rights.seeCheques ? page.cheques.filter(c => isChequeActive(chequeState(c, today))) : [];
    const cheque: Cheque | undefined = [...inHand].sort((a, b) => new Date(a.chequeDate).getTime() - new Date(b.chequeDate).getTime())[0];

    return (
        <div className="@container bg-tint border-t border-separator px-5 py-4">
            <dl aria-label="Collection work in Timely"
                className="grid gap-x-8 gap-y-3 @min-[34rem]:grid-cols-2 @min-[34rem]:gap-y-4 @min-[60rem]:grid-cols-4">
                <Fact label="Last contact">
                    {last ? (
                        <>
                            <span className="block text-secondary font-semibold text-label">
                                {date.short(last.createdAt)} · {firstName(last.authorName || 'Somebody')}
                                <span className="font-normal text-label-2"> · {lastLabel}</span>
                            </span>
                            {last.body?.trim() && <span className="block text-secondary text-label-2 line-clamp-2 break-words">“{last.body.trim()}”</span>}
                        </>
                    ) : <span className="text-secondary text-label-3">Nothing recorded yet</span>}
                </Fact>
                <Fact label="Promise">
                    {promise ? (
                        <>
                            <span className={cx('block text-secondary font-semibold', promise.tone)}>{promise.word}</span>
                            <span className="block text-secondary text-label-2 num">{promise.detail}</span>
                        </>
                    ) : <span className="text-secondary text-label-3">No open promise</span>}
                </Fact>
                <Fact label="Next follow-up">
                    <span className={cx('block text-secondary font-semibold', next.tone === 'late' || next.tone === 'today' ? 'text-warn' : next.tone === 'collected' ? 'text-pos' : next.tone === 'not_set' ? 'text-label-3' : 'text-label')}>
                        {next.tone === 'today' ? 'Today' : next.text}
                    </span>
                    <span className="block text-secondary text-label-2 break-words">{who}</span>
                </Fact>
                <Fact label="Contact">
                    {contact ? (
                        <>
                            <span className="block text-secondary font-semibold text-label break-words">{contact.name || 'The account’s number'}</span>
                            {contact.role && <span className="block text-caption text-label-3 truncate" title={contact.role}>{contact.role}</span>}
                            <span className="block text-secondary text-label-2 num break-words">{contact.number}</span>
                        </>
                    ) : (
                        <span className="text-secondary text-label-3">
                            No phone number ·{' '}
                            <Link to={tabTo('contacts')} className="font-semibold text-accent-text hover:underline">{rights.edit ? 'Add one in Contacts' : 'See Contacts'}</Link>
                        </span>
                    )}
                </Fact>
                {cheque && (
                    <Fact label="Cheque in hand">
                        <span className="block text-secondary font-semibold text-label num">{money.exact(Math.abs(cheque.amount))}</span>
                        <span className="block text-secondary text-label-2">
                            dated {date.short(cheque.chequeDate)} ·{' '}
                            <Link to={`${paths(workspace.slug).cheques}?customer=${customer.id}`} className="font-semibold text-accent-text hover:underline">
                                {page.cheques.length === 1 ? 'In the register' : 'All in the register'}
                            </Link>
                        </span>
                    </Fact>
                )}
            </dl>
        </div>
    );
}

/** The most recent promise somebody answered, for "how the last one ended". */
function latestAnsweredPromise(entries: readonly ActivityEntry[]): { amount: number; on: string | null } | null {
    const answered = new Set(entries.map(e => e.resolvesId).filter(Boolean));
    const promise = entries.find(e => e.kind === 'promise' && answered.has(e.id));
    return promise && promise.promisedAmount !== undefined ? { amount: promise.promisedAmount, on: promise.promisedOn ?? null } : null;
}

/** A fact of the work: its label, then the value. Where the grid is one column (a phone), the label sits beside it. */
const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="min-w-0 grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 @min-[34rem]:block">
        <dt className="text-caption font-semibold text-label-3 @min-[34rem]:mb-1 pt-px">{label}</dt>
        <dd className="min-w-0">{children}</dd>
    </div>
);
